import { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "app/store";
import { useScale } from "common/utils";
import { markParentChatRoomMessage } from "features/message/api";
import { setParentLocale, restoreChildLocale } from "features/shared/i18n";
import { useParentChatSubscribe } from "features/top/useParentChatSubscribe";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { useSidebar } from "./useSidebar";

export const MainLayout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { width, height, scale } = useScale();
  const scrollRef = useRef<HTMLElement>(null);
  const { isOpen, open, close } = useSidebar();

  // チャットメッセージのリアルタイム購読
  useParentChatSubscribe();

  const parentLocale = useSelector(
    (state: RootState) => state.config.parentConfig.locale,
  );
  const childLocale = useSelector(
    (state: RootState) => state.config.userConfig.locale,
  );

  // 既読マークをサーバーに送信（ページ離脱時）+ 子どもの言語に戻す
  useEffect(() => {
    return () => {
      dispatch(markParentChatRoomMessage());
      restoreChildLocale(childLocale);
    };
  }, [dispatch, childLocale]);

  useEffect(() => {
    setParentLocale(parentLocale);
  }, [parentLocale]);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo(0, 0);
  };

  return (
    <div className="flex-col-el flex-center w-full h-full bg-gray2-30 z-[9999]">
      <div className="flex-col-el w-full flex-1 overflow-hidden">
        <div className="flex-col-el w-full h-full">
          {/* Header */}
          <Header onMenuClick={open} />

          {/* Main content */}
          <div
            style={{ height: height - 90 * scale }}
            className="flex-col-el items-center justify-start w-full bg-white/40"
          >
            <main
              ref={scrollRef}
              style={{
                width: width / scale,
                height: (height - 90 * scale) / scale,
                transform: `scale(${scale})`,
              }}
              className="flex-col-el shrink-0 p-6 origin-top overflow-y-auto invisible-scrollbar"
            >
              <Outlet context={{ scrollToTop }} />
            </main>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <Sidebar isOpen={isOpen} onClose={close} />
    </div>
  );
};

// Hook for child components to access scrollToTop
export const useLayoutContext = () => {
  // This can be expanded to use React Router's useOutletContext if needed
  return { scrollToTop: () => {} };
};
